import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { VerificationGate } from "@/components/payments/VerificationGate";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { localized } from "@/lib/intl";

/* Invite link landing: /invite/:token — instant bidding access after terms acceptance.
 * P0: acepta AMBOS documentos legales (paridad con register-to-bid) y guía el
 * gate de verificación en vez de un error genérico sin salida. */
export default function InvitePage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [acceptPlatform, setAcceptPlatform] = useState(false);
  const [acceptAuction, setAcceptAuction] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reg, setReg] = useState(null);
  const [gate, setGate] = useState(null); // {required} => verificación pendiente

  useEffect(() => {
    api.get(`/invites/${token}`).then(({ data }) => setInfo(data)).catch(() => setInfo(false));
  }, [token]);

  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/redeem`, {
        terms_version: 1,
        accept_platform_terms: acceptPlatform,
        accept_auction_terms: acceptAuction,
        locale,
      });
      setReg(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.code === "verification_required") {
        setGate({ required: detail.required || 2 });
      } else {
        toast.error(formatApiErrorDetail(detail) || t("auth.genericError"));
      }
    } finally {
      setBusy(false);
    }
  };

  if (info === false) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="invite-invalid">
        <p className="text-lg font-semibold text-[#0A0A0A]">{t("inv.invalid")}</p>
      </div>
    );
  }
  if (!info) return <div className="max-w-md mx-auto px-6 py-24"><Skeleton className="h-48 rounded-3xl" /></div>;

  return (
    <div className="max-w-md mx-auto px-6 py-20 flex flex-col items-center text-center gap-6" data-testid="invite-page">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1D4D3B]">{t("inv.eyebrow")}</span>
      <h1 className="text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A0A0A]">{t("inv.title")}</h1>
      <p className="text-lg text-[#737373]">{localized(info.event_title, locale)}</p>

      {reg ? (
        <div className="flex flex-col items-center gap-4" data-testid="invite-success">
          <span className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#1D4D3B] text-white text-xl font-semibold">
            #{reg.paddle_number}
          </span>
          <p className="text-sm font-semibold text-[#0A0A0A]">{t("bid.paddleAssigned", { paddle: reg.paddle_number })}</p>
          <Link
            to={`/e/${info.event_slug}`}
            data-testid="invite-go-event"
            className="rounded-full bg-[#0A0A0A] text-white text-[17px] px-[22px] py-[11px] hover:bg-[#2b2b2b] transition-colors"
          >
            {t("inv.goEvent")}
          </Link>
        </div>
      ) : !user ? (
        <Link
          to={`/signin?next=${encodeURIComponent(location.pathname)}`}
          data-testid="invite-signin"
          className="rounded-full bg-[#0A0A0A] text-white text-[17px] px-[22px] py-[11px] hover:bg-[#2b2b2b] transition-colors"
        >
          {t("inv.signin")}
        </Link>
      ) : gate ? (
        <div className="flex flex-col gap-4 w-full text-left" data-testid="invite-verification-gate">
          <div className="flex flex-col gap-1 text-center">
            <p className="text-sm font-semibold text-[#0A0A0A]">{t("inv.verifyTitle")}</p>
            <p className="text-xs text-[#737373]">{t("inv.verifyDesc")}</p>
          </div>
          <VerificationGate requiredLevel={gate.required} onSatisfied={() => setGate(null)} />
        </div>
      ) : (
        <div className="flex flex-col gap-4 w-full" data-testid="invite-redeem-form">
          <p className="text-sm text-[#737373]">{t("inv.desc")}</p>
          {[
            { key: "platform", checked: acceptPlatform, set: setAcceptPlatform, label: t("bid.acceptPlatform"), href: "/legal/terms" },
            { key: "auction", checked: acceptAuction, set: setAcceptAuction, label: t("bid.acceptAuction"), href: `/auction-terms/${info.event_id}` },
          ].map((d) => (
            <div key={d.key} className="rounded-2xl border border-black/10 px-4 py-3.5 flex items-start gap-3 text-left" data-testid={`invite-legal-row-${d.key}`}>
              <Checkbox
                checked={d.checked}
                onCheckedChange={(v) => d.set(Boolean(v))}
                data-testid={`invite-accept-${d.key}`}
                className="mt-0.5"
                aria-label={d.label}
              />
              <button type="button" onClick={() => d.set(!d.checked)} className="flex-1 text-left text-sm text-[#0A0A0A] leading-snug">
                {d.label}
              </button>
              <a
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`invite-view-${d.key}`}
                className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#1D4D3B] hover:underline underline-offset-4"
              >
                {t("bid.viewDocument")}
                <ArrowUpRight className="w-3 h-3" aria-hidden="true" />
              </a>
            </div>
          ))}
          <Button
            onClick={redeem}
            disabled={!acceptPlatform || !acceptAuction || busy}
            data-testid="invite-redeem-button"
            className="rounded-full bg-[#0A0A0A] text-white h-11 hover:bg-[#2b2b2b] disabled:opacity-40"
          >
            {busy ? "…" : t("inv.cta")}
          </Button>
        </div>
      )}
    </div>
  );
}
